import { tap, catchError } from 'rxjs/operators';
import { Injectable } from '@angular/core';

import { State, Action, StateContext } from '@ngxs/store';

import { BcStateContext, BcStateListContext } from '@bayclubs/state';
import {
    PersonService,
    CourtBookingService,
    PersonModel,
    CheckPersonRulesMatrixRequest
} from '@bayclubs/court-booking-employee-api';

import { CoreFacade } from '@core/core.module';

import { PLAYERS_FEATURE_KEY, PlayersStateModel } from './players.state.model';
import * as actions from './players.actions';

const initialState = {
    player: BcStateContext.Empty(),
    players: BcStateListContext.Empty(),
    selectedPlayer: null,
    playerInput: null,
    selectedPlayerRulesMatrix: null
} as PlayersStateModel;

@Injectable({ providedIn: 'root' })
@State({ name: PLAYERS_FEATURE_KEY, defaults: initialState })
export class PlayersState {
    constructor(
        private personApiService: PersonService,
        private coreFacade: CoreFacade,
        private courtBookingApiService: CourtBookingService
    ) {}

    @Action(actions.LoadPlayerAction)
    loadPlayer(ctx: StateContext<PlayersStateModel>, action: actions.LoadPlayerAction) {
        ctx.patchState({ player: BcStateContext.Loading() });
        return this.personApiService.api10PersonIdGet({ id: action.playerInput.playerId }).pipe(
            tap(value => {
                ctx.patchState({ player: BcStateContext.Loaded(value), playerInput: action.playerInput });
            }),
            catchError(err => {
                ctx.patchState({ player: BcStateContext.Error() });
                throw err;
            })
        );
    }

    @Action(actions.SearchPlayersAction)
    searchPlayers(ctx: StateContext<PlayersStateModel>, action: actions.SearchPlayersAction) {
        ctx.patchState({ players: BcStateListContext.Loading() });
        return this.personApiService.api10PersonSearchGet({ searchPhrase: action.search, take: 30 }).pipe(
            tap(value => {
                ctx.patchState({ players: BcStateListContext.Loaded(value.persons) });
            }),
            catchError(err => {
                ctx.patchState({ players: BcStateListContext.Error() });
                throw err;
            })
        );
    }

    @Action(actions.SelectPlayerAction)
    selectPlayer(ctx: StateContext<PlayersStateModel>, action: actions.SelectPlayerAction) {
        ctx.patchState({ selectedPlayer: action.player });
        ctx.dispatch(new actions.CheckPlayerRulesMatrixAction());
    }

    @Action(actions.ChangePlayerAction)
    changePlayer(ctx: StateContext<PlayersStateModel>, action: actions.ChangePlayerAction) {
        const state = ctx.getState();

        const club = this.coreFacade.selectedClub();
        return this.courtBookingApiService
            .api10ClubIdCourtbookingCourtBookingIdChangePlayerPost({
                clubId: club.id,
                courtBookingId: action.courtBookingId,
                changeCourtBookingPlayerRequest: {
                    oldPlayerId: state.player.data.personId,
                    newPlayerId: state.selectedPlayer.personId,
                    invitationText:
                        state.selectedPlayer.personId === null
                            ? state.selectedPlayer.eMail !== null
                                ? state.selectedPlayer.eMail
                                : state.selectedPlayer.phoneNumber
                            : null
                }
            })
            .pipe(
                tap(value => {
                    ctx.dispatch(new actions.ChangePlayerSuccessAction());
                }),
                catchError(err => {
                    throw err;
                })
            );
    }

    @Action(actions.ClearPlayersFormDataAction)
    clearPlayersFormData(ctx: StateContext<PlayersStateModel>) {
        ctx.patchState({
            players: BcStateListContext.Empty(),
            selectedPlayer: null,
            player: BcStateContext.Empty(),
            playerInput: null,
            selectedPlayerRulesMatrix: null
        });
    }

    @Action(actions.ClearPlayersAction)
    clearPlayers(ctx: StateContext<PlayersStateModel>) {
        ctx.patchState({ players: BcStateListContext.Empty(), selectedPlayer: null, selectedPlayerRulesMatrix: null });
    }

    @Action(actions.AddPlayerAction)
    addPlayer(ctx: StateContext<PlayersStateModel>, action: actions.AddPlayerAction) {
        ctx.patchState({
            selectedPlayer: {
                personId: null,
                eMail: action.email,
                phoneNumber: action.phone
            } as PersonModel
        });
    }

    @Action(actions.CheckPlayerRulesMatrixAction)
    checkPlayerRulesMatrix(ctx: StateContext<PlayersStateModel>, action: actions.CheckPlayerRulesMatrixAction) {
        const state = ctx.getState();
        const club = this.coreFacade.selectedClub();
        return this.personApiService
            .api10PersonCheckRulesMatrixPost({
                checkPersonsRulesMatrixRequest: {
                    clubId: club.id,
                    courtId: state.playerInput.courtId,
                    categoryCode: state.playerInput.categoryCode,
                    date: state.playerInput.date,
                    duration: state.playerInput.duration,
                    persons: [
                        {
                            personId: state.selectedPlayer.personId,
                            isMainPlayer: false
                        } as CheckPersonRulesMatrixRequest
                    ]
                }
            })
            .pipe(
                tap(value => {
                    ctx.patchState({ selectedPlayerRulesMatrix: value.items[0] });
                }),
                catchError(err => {
                    throw err;
                })
            );
    }
}
